import { domainError, SHARED_ERROR, type DomainError } from './domain-error';
import { err, ok, type Result } from './result';

const CENTS_PER_UNIT = 100;

/**
 * Valor monetário em **centavos inteiros** (ADR-106).
 *
 * Pelo mesmo motivo de `Hours`: acumular reais em ponto flutuante produz centavos
 * fantasma. Custo ausente é `null` no modelo — nunca `Money.zero`, porque
 * "não informado" e "custa zero" são coisas diferentes (RN-055).
 */
export class Money {
  static readonly ZERO = new Money(0);

  private constructor(readonly cents: number) {}

  static fromCents(cents: number): Result<Money, DomainError> {
    if (!Number.isInteger(cents)) {
      return err(
        domainError(SHARED_ERROR.INVALID_MONEY, 'Valor monetário deve ser inteiro em centavos.', {
          cents,
        }),
      );
    }
    return ok(new Money(cents));
  }

  static fromUnits(value: number): Result<Money, DomainError> {
    if (!Number.isFinite(value)) {
      return err(domainError(SHARED_ERROR.INVALID_MONEY, 'Valor monetário inválido.', { value }));
    }
    return ok(new Money(Math.round(value * CENTS_PER_UNIT)));
  }

  static sum(values: readonly Money[]): Money {
    let total = 0;
    for (const value of values) total += value.cents;
    return new Money(total);
  }

  plus(other: Money): Money {
    return new Money(this.cents + other.cents);
  }

  minus(other: Money): Money {
    return new Money(this.cents - other.cents);
  }

  /** Arredonda para o centavo mais próximo — ex.: custo/hora × horas trabalhadas. */
  times(factor: number): Money {
    return new Money(Math.round(this.cents * factor));
  }

  isZero(): boolean {
    return this.cents === 0;
  }

  compare(other: Money): number {
    return this.cents - other.cents;
  }

  toUnits(): number {
    return this.cents / CENTS_PER_UNIT;
  }

  /** `123456 → "R$ 1.234,56"` */
  format(): string {
    const negative = this.cents < 0;
    const abs = Math.abs(this.cents);
    const units = Math.floor(abs / CENTS_PER_UNIT);
    const remainder = abs % CENTS_PER_UNIT;
    const grouped = units.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    const body = `R$ ${grouped},${String(remainder).padStart(2, '0')}`;
    return negative ? `-${body}` : body;
  }

  toString(): string {
    return this.format();
  }
}
